import {objectFromEntries} from '@benchling/js-utils';
import {StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React from 'react';

import {ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT} from 'auth/DefaultSchemaCollaboratorsConfiguration';
import * as BenchlingUtil from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {BaseFieldTableProps} from 'registry/field/FieldTable';
import setWindowTitleForSchema from 'results/assay-schemas/setWindowTitleForSchema';
import NoOwnersSchemaMessage from 'schemas/NoOwnersSchemaMessage';
import SchemaSettingsExportDropdown from 'schemas/SchemaSettingsExportDropdown';
import {SCHEMA_TYPES} from 'schemas/spec/SchemaConfigConstants';
import {getSchemaOwnerChoices} from 'schemas/spec/SchemaSpecUtil';
import SettingsPageContainer from 'settings/SettingsPageContainer';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {ConnectionSchemaEditorQuery} from './__generated/ConnectionSchemaEditorQuery';
import {ConnectionSchemaQuery} from './__generated/ConnectionSchemaQuery';
import {ConnectionSchema} from './ConnectionSchemaConstants';
import ConnectionSchemaCreateFlow from './ConnectionSchemaCreateFlow';
import {connectionSchemasURL} from './ConnectionSchemaDAO';
import ExistingConnectionSchemaEditor from './ExistingConnectionSchemaEditor';

type ConnectionSchemaNode = ExtractByTypename<
  ConnectionSchemaQuery['connectionSchema'],
  'ConnectionSchemaNode'
>;

type Organization = NonNullable<ConnectionSchemaEditorQuery['currentTenantOrganizations']>[number];

export interface ConnectionSchemaEditorProps {
  schemaId?: string;
}

export interface ConnectionSchemaEditorQueriedProps {
  // Human-readable summaries of dependent computed fields, lookup fields, etc
  schemaFieldDependentsSummariesById: BaseFieldTableProps['schemaFieldDependentsSummariesById'];
  organizations: Array<Organization>;
  schema: ConnectionSchema | null;
  refetch: () => void;
}

export function ConnectionSchemaEditor(
  props: ConnectionSchemaEditorProps & ConnectionSchemaEditorQueriedProps
): JSX.Element {
  const ownerChoices = getSchemaOwnerChoices();

  if (!props.schemaId && ownerChoices.length === 0) {
    return <NoOwnersSchemaMessage />;
  }

  setWindowTitleForSchema(props.schema, SCHEMA_TYPES.CONNECTION);

  return (
    <SettingsPageContainer
      title="Connection Schemas"
      subtitle={props.schemaId ? props.schema!.name : `Create Connection Schema`}
      onClickTitle={(): void => {
        BenchlingUtil.navigate(null, connectionSchemasURL());
      }}
      toolbar={
        props.schema && (
          <SchemaSettingsExportDropdown
            schemaId={props.schema.id}
            schemaType={SCHEMA_TYPES.CONNECTION as unknown as ModelTypeConstants}
          />
        )
      }
    >
      {props.schemaId ? (
        <ExistingConnectionSchemaEditor
          schemaId={props.schemaId}
          schema={props.schema!}
          schemaFieldDependentsSummariesById={props.schemaFieldDependentsSummariesById}
          ownerChoices={ownerChoices}
          refetch={props.refetch}
        />
      ) : (
        <ConnectionSchemaCreateFlow
          schemaFieldDependentsSummariesById={props.schemaFieldDependentsSummariesById}
          ownerChoices={ownerChoices}
          organizations={props.organizations}
        />
      )}
    </SettingsPageContainer>
  );
}

export const CONNECTION_SCHEMA_EDITOR_QUERY: DocumentNode = gql`
  query ConnectionSchemaEditorQuery @skipAudit @engComponent(name: INSTRUMENTS) {
    currentTenantOrganizations {
      ... on OrganizationNode {
        ...OrganizationDefaultSchemaCollaboratorsConfiguration
      }
    }
  }
  ${ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT}
`;

export const CONNECTION_SCHEMA_QUERY: DocumentNode = gql`
  query ConnectionSchemaQuery($id: StrictString!) @skipAudit @engComponent(name: INSTRUMENTS) {
    connectionSchema(id: $id) {
      ... on ConnectionSchemaNode {
        id
        name
        allFields {
          id
          apiId
          dependentsSummaries
        }
        allFieldsJson
        fieldsJson
        integrationAdapterId
        organization
        prefix
        sqlIdentifier
        archiveRecord {
          purpose
        }
        hasData
        ... on PoliceableInterface {
          id
          permissions {
            ...SchemaPermissionsGraphQL
          }
        }
        equipmentIds
      }
    }
  }
  ${getFragmentWrapper('SchemaPermissions').fragment}
`;

function QueryingConnectionSchemaEditor(props: ConnectionSchemaEditorProps): JSX.Element {
  const queryResult = useBenchlingQuery<ConnectionSchemaEditorQuery>(CONNECTION_SCHEMA_EDITOR_QUERY);
  const connectionSchemaResult = useSkippableBenchlingQuery<ConnectionSchemaQuery>(CONNECTION_SCHEMA_QUERY, {
    variables: {id: props.schemaId},
    skip: props.schemaId == null,
    renderCachedDataWhenRefetching: true,
  });
  if (
    connectionSchemaResult.type === QueryResultType.PENDING ||
    connectionSchemaResult.type === QueryResultType.ERROR ||
    queryResult.type !== QueryResultType.SUCCESS
  ) {
    return <CenteredHorizontalThrobber />;
  }

  function connectionSchemaToSchema(connectionSchema: ConnectionSchemaNode): ConnectionSchema {
    return {
      ...connectionSchema,
      fields: connectionSchema.fieldsJson,
      allFields: connectionSchema.allFieldsJson,
      equipmentIds: connectionSchema.equipmentIds,
    };
  }

  let connectionSchema: ConnectionSchemaNode | null = null;
  if (connectionSchemaResult.type === QueryResultType.SUCCESS) {
    const connectionSchemaOrInaccessible = connectionSchemaResult.data.connectionSchema;
    if (!isInaccessible(connectionSchemaOrInaccessible)) {
      connectionSchema = connectionSchemaOrInaccessible;
    }
  }

  const schemaFieldDependentsSummariesById = connectionSchema
    ? objectFromEntries(connectionSchema.allFields.map((field) => [field.apiId, field.dependentsSummaries]))
    : {};

  const organizations = queryResult.data.currentTenantOrganizations;

  return (
    <ConnectionSchemaEditor
      schemaId={props.schemaId}
      schema={connectionSchema ? connectionSchemaToSchema(connectionSchema) : null}
      schemaFieldDependentsSummariesById={schemaFieldDependentsSummariesById}
      organizations={organizations}
      refetch={() => connectionSchemaResult.refetch({id: props.schemaId})}
    />
  );
}

export const connectionSchemaEditorStyles = StyleSheet.create({
  fieldSection: {
    marginBottom: '32px',
  },
});

export default QueryingConnectionSchemaEditor;
